/**
 * AccountColorManager - Manages unique color assignments for accounts
 * 
 * Features:
 * - Generates visually distinct colors using HSL color space with golden ratio distribution
 * - Persists color assignments in sessionStorage for consistency within a session
 * - Validates colors for WCAG AA accessibility compliance (4.5:1 contrast ratio)
 */

export interface Account {
  id: string;
  name: string;
}

export interface ColorGenerationOptions {
  accounts: Account[];
  sessionKey?: string;
}

const GOLDEN_RATIO = 0.618033988749895;
const DEFAULT_SATURATION = 65; // Vibrant but not overwhelming
const DEFAULT_LIGHTNESS = 55; // Balanced for light/dark themes
const WCAG_AA_CONTRAST_RATIO = 4.5;
const DEFAULT_SESSION_KEY = 'account-colors';

/**
 * AccountColorManager class for generating and managing account colors
 */
export class AccountColorManager {
  private colorMap: Map<string, string>;
  private sessionKey: string;

  constructor(accounts: Account[], sessionKey: string = DEFAULT_SESSION_KEY) {
    this.sessionKey = sessionKey;
    this.colorMap = new Map();
    
    // Try to restore from session storage
    const restored = this.restoreFromSession();
    
    if (restored) {
      // Validate that all current accounts have colors
      const missingAccounts = accounts.filter(acc => !this.colorMap.has(acc.id));
      if (missingAccounts.length > 0) {
        // Generate colors for missing accounts
        this.generateColorsForAccounts(missingAccounts, this.colorMap.size);
        this.saveToSession();
      }
    } else {
      // Generate fresh colors for all accounts
      this.generateColorsForAccounts(accounts, 0);
      this.saveToSession();
    }
  }

  /**
   * Generate colors for a list of accounts
   */
  private generateColorsForAccounts(accounts: Account[], startIndex: number): void {
    accounts.forEach((account, idx) => {
      const color = generateAccountColor(account.id, startIndex + idx, startIndex + accounts.length);
      this.colorMap.set(account.id, color);
    });
  }

  /**
   * Get color for a specific account
   */
  getColor(accountId: string): string {
    return this.colorMap.get(accountId) || generateAccountColor(accountId, 0, 1);
  }

  /**
   * Get all color assignments
   */
  getAllColors(): Map<string, string> {
    return new Map(this.colorMap);
  }

  /**
   * Regenerate all colors (clears session storage)
   */
  regenerateColors(accounts: Account[]): void {
    this.colorMap.clear();
    this.generateColorsForAccounts(accounts, 0);
    this.saveToSession();
  }

  /**
   * Save color map to session storage
   */
  private saveToSession(): void {
    try {
      const data = Array.from(this.colorMap.entries());
      sessionStorage.setItem(this.sessionKey, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save account colors to session storage:', error);
    }
  }

  /**
   * Restore color map from session storage
   * Returns true if restoration was successful
   */
  private restoreFromSession(): boolean {
    try {
      const stored = sessionStorage.getItem(this.sessionKey);
      if (stored) {
        const data = JSON.parse(stored) as [string, string][];
        this.colorMap = new Map(data);
        return true;
      }
    } catch (error) {
      console.warn('Failed to restore account colors from session storage:', error);
    }
    return false;
  }

  /**
   * Validate contrast ratio for a color against a background
   */
  static validateContrast(color: string, background: string = '#ffffff'): boolean {
    const ratio = calculateContrastRatio(color, background);
    return ratio >= WCAG_AA_CONTRAST_RATIO;
  }
}

/**
 * Generate a unique color for an account using golden ratio distribution
 */
export function generateAccountColor(accountId: string, index: number, total: number): string {
  // Use golden ratio for even hue distribution
  const hue = (index * GOLDEN_RATIO * 360) % 360;
  
  return `hsl(${Math.round(hue)}, ${DEFAULT_SATURATION}%, ${DEFAULT_LIGHTNESS}%)`;
}

/**
 * Calculate relative luminance of a color
 * Based on WCAG 2.0 formula
 */
function getRelativeLuminance(color: string): number {
  const rgb = parseColor(color);
  
  // Convert RGB to relative luminance
  const [r, g, b] = rgb.map(channel => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculate contrast ratio between two colors
 * Based on WCAG 2.0 formula
 */
export function calculateContrastRatio(color1: string, color2: string): number {
  const lum1 = getRelativeLuminance(color1);
  const lum2 = getRelativeLuminance(color2);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Parse color string to RGB values
 * Supports hex, rgb, rgba, hsl, hsla formats
 */
function parseColor(color: string): [number, number, number] {
  // Handle hex colors
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return [r, g, b];
  }
  
  // Handle rgb/rgba colors
  if (color.startsWith('rgb')) {
    const match = color.match(/\d+/g);
    if (match && match.length >= 3) {
      return [parseInt(match[0]), parseInt(match[1]), parseInt(match[2])];
    }
  }
  
  // Handle hsl/hsla colors
  if (color.startsWith('hsl')) {
    const match = color.match(/[\d.]+/g);
    if (match && match.length >= 3) {
      const h = parseFloat(match[0]) / 360;
      const s = parseFloat(match[1]) / 100;
      const l = parseFloat(match[2]) / 100;
      return hslToRgb(h, s, l);
    }
  }
  
  // Default to white if parsing fails
  console.warn(`Unable to parse color: ${color}, defaulting to white`);
  return [255, 255, 255];
}

/**
 * Convert HSL to RGB
 */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Utility function to validate if a color meets WCAG AA standards
 */
export function validateColorAccessibility(color: string, background: string = '#ffffff'): boolean {
  return AccountColorManager.validateContrast(color, background);
}
